/**
 * Formatting and the app's entire copy deck. Money arrives as integer cents and is never turned
 * into a float on the way out; the euro/cent split is done with integer arithmetic so a rounding
 * artefact can never appear on the hero figure.
 */
import type {
	AccountLifecycle,
	AccountRole,
	Flag,
	OfferMechanic,
	Regime,
} from "../../server/types.ts";

const AMOUNT = new Intl.NumberFormat("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const QTY = new Intl.NumberFormat("nl-NL", { maximumFractionDigits: 3 });

/** `-1234` becomes `"-12,34"`. No currency symbol: the layout places that itself. */
export function amount(cents: number): string {
	const whole = Math.trunc(Math.abs(cents) / 100);
	const rest = Math.abs(cents) % 100;
	return `${cents < 0 ? "-" : ""}${AMOUNT.format(whole + rest / 100)}`;
}

export function euros(cents: number): string {
	return `\u20ac\u00a0${amount(cents)}`;
}

export function signedEuros(cents: number): string {
	return `${cents > 0 ? "+" : ""}${euros(cents)}`;
}

/** Weight-priced goods are one line of fractional quantity, so this is not an integer format. */
export function quantity(qty: number): string {
	return QTY.format(qty);
}

export function percent(fraction: number): string {
	return `${Math.round(fraction * 100)}%`;
}

/** `"12,34"`, `"12.34"` and `"1234,"` all parse; anything else is rejected rather than coerced. */
export function parseAmountCents(input: string): number | null {
	const cleaned = input.trim().replace(/^\u20ac\s*/, "").replace(",", ".");
	if (cleaned === "" || !/^-?\d*(\.\d{0,2})?$/.test(cleaned)) return null;
	const value = Number(cleaned);
	if (!Number.isFinite(value)) return null;
	return Math.round(value * 100);
}

export function relativeTime(at: number, now: number): string {
	const seconds = Math.round((now - at) / 1000);
	if (seconds < 0) return "in the future";
	if (seconds < 45) return "just now";
	if (seconds < 5400) return `${Math.round(seconds / 60)} min ago`;
	const hours = Math.round(seconds / 3600);
	if (hours < 36) return `${hours} h ago`;
	return `${Math.round(hours / 24)} d ago`;
}

/** A span rather than a point: used for "how long is this token still good for". */
export function duration(ms: number): string {
	const seconds = Math.max(0, Math.round(ms / 1000));
	if (seconds < 90) return `${seconds} s`;
	if (seconds < 5400) return `${Math.round(seconds / 60)} min`;
	const hours = Math.round(seconds / 3600);
	if (hours < 36) return `${hours} h`;
	return `${Math.round(hours / 24)} d`;
}

const STAMP = new Intl.DateTimeFormat("en-GB", {
	weekday: "short",
	day: "numeric",
	month: "short",
	hour: "2-digit",
	minute: "2-digit",
	timeZone: "Europe/Amsterdam",
});

export function stamp(at: number): string {
	return STAMP.format(new Date(at));
}

const DAY = new Intl.DateTimeFormat("en-GB", {
	weekday: "short",
	day: "numeric",
	month: "short",
	timeZone: "Europe/Amsterdam",
});

/** ISO `YYYY-MM-DD` to `"Mon 24 Aug"`. Bonus weeks are dates, so no time is shown. */
export function isoDay(iso: string): string {
	const at = Date.parse(`${iso}T12:00:00Z`);
	if (Number.isNaN(at)) return iso;
	return DAY.format(new Date(at));
}

export function mechanicLabel(mechanic: OfferMechanic): string {
	switch (mechanic.kind) {
		case "PERCENT":
			return `${mechanic.percent}% off`;
		case "AMOUNT_UNIT":
			return `${euros(mechanic.amountCents)} off each`;
		case "UNIT_PRICE":
			return `${euros(mechanic.priceCents)} each`;
		case "N_FOR_M":
			return `${mechanic.m}+${mechanic.n - mechanic.m} free`;
		case "BASKET_PERCENT":
			return `${mechanic.percent}% off the basket`;
		case "BASKET_AMOUNT":
			return `${euros(mechanic.amountCents)} off from ${euros(mechanic.minSpendCents)}`;
		case "UNSUPPORTED":
			return `not modelled: ${mechanic.raw}`;
	}
}

export type FlagCopy = { label: string; detail: string; severity: "warn" | "info" };

/** ALGORITHM.md §16: a recommendation resting on speculation has to say so, in words. */
export const FLAG_COPY: Record<Flag, FlagCopy> = {
	APPROX_VALUATION: {
		label: "Estimated, not proven",
		detail:
			"The solver hit a size guard or an incomplete coverage list, so this discount is its best assignment rather than a proven optimum.",
		severity: "warn",
	},
	STALE_BOX: {
		label: "Stale bonus box",
		detail:
			"A box in play was last read before the current bonus week started. Those offers may no longer exist on the account.",
		severity: "warn",
	},
	UNKNOWN_BARCODES: {
		label: "Unknown barcodes",
		detail:
			"Some scanned codes matched no AH product. They are in the basket but contribute nothing to the discount, so the real saving may be higher.",
		severity: "warn",
	},
	TRUNCATED_COVERAGE: {
		label: "Incomplete coverage",
		detail:
			"AH returned fewer covered products than it claimed for an offer, so items it actually covers may have been treated as ineligible.",
		severity: "warn",
	},
	UNSUPPORTED_MECHANIC: {
		label: "Unmodelled mechanic",
		detail:
			"An offer uses a discount shape this app does not model. It is valued at zero deliberately rather than guessed at, so check it by hand.",
		severity: "warn",
	},
	INFERRED_CATEGORY: {
		label: "Category guessed",
		detail: "The trip category was inferred from the basket, not chosen. Correcting it is how the guess gets better.",
		severity: "info",
	},
	NO_CATEGORY: {
		label: "No category",
		detail:
			"No category, so specialised cards were not scoped. The pick can put foreign items on a card that has a profile to protect.",
		severity: "info",
	},
	MISSING_PRICES: {
		label: "Prices not refreshed",
		detail: "Some prices come from an older snapshot, so line totals and the discount may be off by whatever moved.",
		severity: "warn",
	},
};

export const LIFECYCLE_COPY: Record<AccountLifecycle, { label: string; detail: string }> = {
	FRESH: { label: "Fresh", detail: "Never shopped on. Reservoir for seeding a new profile." },
	SEEDING: { label: "Seeding", detail: "Being taught a habit; its box is not expected to pay yet." },
	WARM: { label: "Warm", detail: "Has a profile and a box worth solving against." },
	STALE: { label: "Stale", detail: "Has not been used in long enough that its profile is decaying." },
	RETIRED: { label: "Retired", detail: "Out of the pool. Never considered." },
};

export const ROLE_COPY: Record<AccountRole, string> = {
	PRIMARY: "Primary",
	BACKUP: "Backup",
	EXCLUDED: "Excluded",
};

export const REGIME_COPY: Record<Regime, { label: string; detail: string }> = {
	HARVEST: {
		label: "Harvest",
		detail: "There is real discount on the table, so the pick maximises what this basket pays out now.",
	},
	INVEST: {
		label: "Invest",
		detail:
			"No card has a worthwhile box for this basket, so the pick is the card whose future boxes this spend improves most.",
	},
};

/** ALGORITHM.md §5.2. */
export const TIER_COPY: Record<number, string> = {
	0: "Primary for this category",
	1: "Backup for this category",
	2: "Generalist, no category links",
	3: "Belongs to another category",
	4: "Fresh reservoir account",
};

/** The card number is only ever shown in full after a verified activation (§12). */
export function maskedCard(card: string | null): string {
	if (card === null) return "no card";
	if (card.length <= 4) return card;
	return `\u2022\u2022\u2022\u2022 ${card.slice(-4)}`;
}

/** `2623064539859` reads as `2623 0645 3985 9` on a screen held at arm's length. */
export function groupDigits(card: string): string {
	return card.replace(/(.{4})/g, "$1 ").trim();
}
